import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop, DropTargetMonitor, useDrag } from 'react-dnd';
import { CloseRounded } from '@mui/icons-material';
import { Tooltip, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import {
  removeParameter,
  removeParameterRightOperand,
} from '../../../redux/Calculations/CalculationsActions';
import { fontSize } from '../../..';
import isArray from 'lodash/isArray';

interface DragItem {
  index: number;
  id: string;
  type: string;
  parameterId: string;
}

interface SelectedParameterProps {
  propKey: string;
  paramSource: any;
  index?: number;
  value?: string;
  moveCard?: (dragIndex: number, hoverIndex: number) => void;
  calculationFlowUID?: string;
  handleSourceCardCrossButton?: (sourceId: string, sourceIndex: number) => void;
  updateParameterIndex?: (index: number, dragIndex: number, type: any) => void;

  conditionFilterId?: string;
  filterIdx?: number;
  setSource?: Dispatch<SetStateAction<any>>;
  isBetweenOperandIndex?: number;
}

const SelectedParameter = ({
  propKey,
  paramSource,
  index = 0,
  value = '',
  moveCard,
  calculationFlowUID,
  handleSourceCardCrossButton,
  updateParameterIndex,

  conditionFilterId,
  filterIdx,
  isBetweenOperandIndex,
  setSource,
}: SelectedParameterProps) => {
  const { parameters, calculationSession } = useSelector((state: any) => {
    return {
      parameters: state.parameters,
      calculationSession: state.calculations.properties[propKey]?.currentCalculationSession,
    };
  });
  const dispatch = useDispatch();

  const [displayName, setDisplayName] = useState('');
  const [sourceCardHoverActive, setSourceCardHoverActive] = useState(false);
  const [alert, setAlert] = useState<any>(null);
  const ref = useRef<HTMLDivElement>(null);

  const IfElse = useMemo(() => {
    return (
      calculationSession?.calculationInfo?.flows[calculationSession?.activeFlow]?.[0]?.flow ===
      'IfElse'
    );
  }, [calculationSession?.activeFlow, calculationSession?.calculationInfo?.flows]);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: string | symbol | null }>({
    accept: ['source-card', 'parameter-card'],
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      if (moveCard) {
        moveCard(dragIndex, hoverIndex);
      }

      if (updateParameterIndex) {
        updateParameterIndex(hoverIndex, dragIndex, item);
      }

      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag<DragItem, unknown, { isDragging: boolean }>({
    type: 'parameter-card',
    item: (): DragItem => {
      return {
        parameterId: isArray(paramSource)
          ? paramSource[isBetweenOperandIndex as number]
          : paramSource?.id,
        id: value,
        index: index || 0,
        type: 'parameter-card',
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  const handleCrossButtonClick = () => {
    if (IfElse) {
      if (!conditionFilterId || filterIdx === undefined) return;
      dispatch(removeParameterRightOperand(propKey, conditionFilterId, filterIdx));
      if (setSource) setSource([]);
    } else if (handleSourceCardCrossButton && typeof index === 'number') {
      handleSourceCardCrossButton(value, index);
    }
    if (calculationFlowUID && !IfElse) {
      dispatch(removeParameter(propKey, paramSource?.id, calculationFlowUID));
    }
  };

  useEffect(() => {
    if (IfElse) {
      if (!conditionFilterId || filterIdx === undefined) return;
      const source =
        calculationSession?.calculationInfo?.conditionFilters[conditionFilterId][0].conditions[
          filterIdx
        ].parameterId;
      const param = parameters.savedParameters?.find(
        (param: any) => param.parameterId === source[isBetweenOperandIndex as number],
      );
      setDisplayName(param?.name || '');
    } else {
      const param = parameters.savedParameters?.find(
        (param: any) => param.parameterId === paramSource?.id,
      );
      setDisplayName(param?.name || '');
    }
  }, [calculationSession?.activeFlow, calculationSession?.calculationInfo?.conditionFilters]);

  return (
    <div
      ref={IfElse ? null : ref}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        opacity: isDragging ? 0 : 1,
        cursor: IfElse ? 'default' : 'move',
        position: 'relative',
        border: '1px solid rgb(211, 211, 211)',
        borderRadius: IfElse ? "4px" : '3px',
        margin: IfElse ? '-3px auto' : '',
        fontSize: fontSize.medium,
        padding: '0px 5px 0px 5px',
        height: '24px',
        minHeight: '24px',
        overflow: 'hidden',
        width: IfElse ? '101%' : 'auto',
      }}
      data-handler-id={handlerId}
      onMouseEnter={() => setSourceCardHoverActive(true)}
      onMouseLeave={() => setSourceCardHoverActive(false)}
    >
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '7px',
          userSelect: 'none',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <button
          type="button"
          className="buttonCommon columnClose"
          title="Remove field"
          onClick={handleCrossButtonClick}
          style={{
            width: '13px',
            height: '13px',
            flexShrink: 0,
            marginLeft: '4px',
            marginRight: '4px',
          }}
        >
          <CloseRounded
            style={{
              fontSize: fontSize.semiLarge,
              display: 'block',
            }}
          />
        </button>

        <Tooltip title={displayName}>
          <span
            style={{
              margin: 0,
              userSelect: 'none',
              alignContent: 'center',
            }}
          >
            {displayName}
          </span>
        </Tooltip>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          // marginRight: calculationFlowUID ? "8px" : "",
        }}
      >
        <Tooltip title="Parametres" placement="top">
          <IconButton size="small">
            <img src="/Parameter_icon.svg" alt="parameter-icon" style={{ height: '0.9rem' }} />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};

export default SelectedParameter;
